import { hash, toUnitFloat } from "daedalus-core";
import { CURRENCY_MAX } from "../currency/coins.ts";
import { isPlainRecord } from "../rules/checks.ts";
import { textDigest } from "../rules/version.ts";
import { MAX_ITEM_STACK, type Rules } from "../rules/types.ts";
import { BAG_SLOTS } from "./bag.ts";

/**
 * Loot tables and the loot roll (ADR-091 decision 18). A loot table is world content, `tools/dev-server/loot/<id>.json`,
 * checked here against the rules' items; the roll of a table for one character at one chest comes from the shared
 * seeded hash over the world, the chest's object id, the character and each entry's place, so a claim tried twice rolls
 * the same loot on the page, the map server and a tool alike. A creature's table may also hold one coin entry, copper,
 * which only a creature gives (ADR-091's amendment for P2.8-T13, decision 2); a kill's roll is `rollKillLoot`, under a
 * seed of its own over the kill's whole key. Pure.
 */

/**
 * The most entries a table holds, which is also the most lines a chest holds for a character (`chest_loot.line` 0 to 7).
 */
export const MAX_LOOT_ENTRIES = 8;

/**
 * The longest loot table id and name, in characters.
 */
export const MAX_LOOT_ID_CHARS = 48;
export const MAX_LOOT_NAME_CHARS = 48;

/**
 * A whole percent: an entry's chance runs from 1 to this.
 */
const PERCENT = 100;

/**
 * A loot table's id: the library's id rule (lower-case letters, digits and `-`, starting with a letter or digit), at
 * most `MAX_LOOT_ID_CHARS` long, since a table is kept in the library under its id.
 */
const LOOT_ID = new RegExp(`^[a-z0-9][a-z0-9-]{0,${MAX_LOOT_ID_CHARS - 1}}$`);

/**
 * The rule a loot table's id follows, in words.
 */
export const LOOT_ID_RULE = `1 to ${MAX_LOOT_ID_CHARS} lower-case letters, digits and -, starting with a letter or digit`;
const TABLE_FIELDS = ["id", "name", "entries"] as const;
const ENTRY_FIELDS = ["item", "min", "max", "chance"] as const;
const COIN_FIELDS = ["coin", "chance"] as const;

/**
 * The field that marks an entry as a coin entry.
 */
const COIN_FIELD = "coin";

/**
 * The seed of the loot roll, apart from every other roll's.
 */
const LOOT_SEED = 0x4c4f4f54;

/**
 * The seed of a kill's roll, apart from the chest's and every other roll's.
 */
export const KILL_LOOT_SEED = 0x4b494c4c;

/**
 * The least share of a creature template's health, in whole percent rounded up, a character must have dealt to receive
 * a drop from its fall (ADR-091's amendment for P2.8-T13, decision 1). 0 makes the recipients the XP recipients.
 */
export const DROP_SHARE_PERCENT = 10;

/**
 * Which decision of an entry a digest settles: whether it gives, and how many.
 */
const ROLL_CHANCE = 0;
const ROLL_QUANTITY = 1;

/**
 * An item entry of a loot table: an item the rules hold, the least and most units it gives, and its chance in whole
 * percents.
 */
export interface ItemLootEntry {
  readonly item: string;
  readonly min: number;
  readonly max: number;
  readonly chance: number;
}

/**
 * A coin entry of a loot table: the least and most copper it gives, each 1 to `CURRENCY_MAX`, and its chance in whole
 * percents. A table holds at most one, and only a creature gives it: the compile refuses a chest naming such a table.
 */
export interface CoinLootEntry {
  readonly coin: readonly [number, number];
  readonly chance: number;
}

/**
 * One entry of a loot table, an item entry or a coin entry.
 */
export type LootEntry = ItemLootEntry | CoinLootEntry;

/**
 * A loot table: its id (the file's name), the name the editor lists it by, and 1 to 8 entries, each rolled on its own.
 */
export interface LootTable {
  readonly id: string;
  readonly name: string;
  readonly entries: readonly LootEntry[];
}

/**
 * The verdict on one loot table document.
 */
export type LootTableVerdict = { readonly ok: true; readonly table: LootTable } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * What a roll is fixed by: the world's id, the chest's authored object id and the character's id.
 */
export interface LootKey {
  readonly world: string;
  readonly object: string;
  readonly character: string;
}

/**
 * What a kill's roll is fixed by: the world's id, the map's id, the creature's definition id, its slot, the tick of
 * its fall and the recipient character's id. A slot is reused, so the tick tells two kills of one definition apart and
 * the slot two creatures of one definition felled in one tick.
 */
export interface KillLootKey {
  readonly world: string;
  readonly map: string;
  readonly definition: string;
  readonly slot: number;
  readonly tick: number;
  readonly character: string;
}

/**
 * What a kill's roll gives one recipient: its item lines in entry order and its copper, 0 for none.
 */
export interface KillLoot {
  readonly lines: LootLine[];
  readonly coin: number;
}

/**
 * One line a roll gives: the entry's place in its table (0 to 7), the item and the units.
 */
export interface LootLine {
  readonly line: number;
  readonly item: string;
  readonly quantity: number;
}

/**
 * Whether an entry is a coin entry.
 *
 * @param entry The entry.
 * @returns True for a coin entry, false for an item entry.
 */
export function isCoinEntry(entry: LootEntry): entry is CoinLootEntry {
  return COIN_FIELD in entry;
}

/**
 * Whether a table holds a coin entry, which only a creature gives.
 *
 * @param table The table.
 * @returns True when it does.
 */
export function holdsCoins(table: LootTable): boolean {
  return table.entries.some(isCoinEntry);
}

/**
 * An entry as a table's file and the compiled loot write it: its fields in the order an entry of its form is written.
 *
 * @param entry The entry.
 * @returns A fresh record, plain JSON data.
 */
export function lootEntryData(entry: LootEntry): ItemLootEntry | CoinLootEntry {
  return isCoinEntry(entry) ? { coin: [entry.coin[0], entry.coin[1]], chance: entry.chance } : { item: entry.item, min: entry.min, max: entry.max, chance: entry.chance };
}

/**
 * Whether text is a loot table's id.
 *
 * @param id The text.
 * @returns True when it follows the rule.
 */
export function isLootTableId(id: string): boolean {
  return LOOT_ID.test(id);
}

/**
 * Why text is not a loot table's id.
 *
 * @param id The text.
 * @returns The sentence, with the rule.
 */
export function notLootTableId(id: string): string {
  return `${id} is not a loot table id; use ${LOOT_ID_RULE}`;
}

/**
 * Whether a value is a whole number within a range.
 *
 * @param value The value.
 * @param min Least allowed.
 * @param max Most allowed.
 * @returns True when it is.
 */
function wholeIn(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Checks one entry, reporting each problem at its place. An entry's `max` is held to what an empty bag holds of its
 * item (`BAG_SLOTS` stacks of the item's size), since a take is all or nothing for a line and a line no bag ever fits
 * could never be taken.
 *
 * @param value The entry.
 * @param path Its path, `entries[n]`.
 * @param rules The rules whose items an entry names.
 * @param fail Where a problem goes.
 * @returns The entry, or null.
 */
function entryOf(value: unknown, path: string, rules: Rules, fail: (message: string) => void): LootEntry | null {
  if (!isPlainRecord(value)) {
    fail(`${path} must be an object`);
    return null;
  }
  if (COIN_FIELD in value) {
    return coinEntryOf(value, path, fail);
  }
  let ok = true;
  const item = value["item"];
  if (typeof item !== "string" || !rules.items.has(item)) {
    fail(`${path}.item names ${String(item)}, which the rules' items table does not hold`);
    ok = false;
  }
  const min = value["min"];
  const max = value["max"];
  if (!wholeIn(min, 1, MAX_ITEM_STACK)) {
    fail(`${path}.min must be a whole number from 1 to ${MAX_ITEM_STACK}`);
    ok = false;
  }
  if (!wholeIn(max, 1, MAX_ITEM_STACK)) {
    fail(`${path}.max must be a whole number from 1 to ${MAX_ITEM_STACK}`);
    ok = false;
  }
  if (wholeIn(min, 1, MAX_ITEM_STACK) && wholeIn(max, 1, MAX_ITEM_STACK) && min > max) {
    fail(`${path}.min ${min} is above ${path}.max ${max}`);
    ok = false;
  }
  const stack = typeof item === "string" ? rules.items.get(item)?.stack : undefined;
  if (stack !== undefined && wholeIn(max, 1, MAX_ITEM_STACK) && max > BAG_SLOTS * stack) {
    fail(`${path}.max ${max} is above ${BAG_SLOTS * stack}, the most ${item as string} an empty bag holds (${BAG_SLOTS} slots of ${stack})`);
    ok = false;
  }
  const chance = value["chance"];
  if (!wholeIn(chance, 1, PERCENT)) {
    fail(`${path}.chance must be a whole percent from 1 to ${PERCENT}`);
    ok = false;
  }
  for (const key of Object.keys(value)) {
    if (!(ENTRY_FIELDS as readonly string[]).includes(key)) {
      fail(`${path}.${key} is not an entry field`);
      ok = false;
    }
  }
  return ok ? { item: item as string, min: min as number, max: max as number, chance: chance as number } : null;
}

/**
 * Checks one coin entry, reporting each problem at its place: `coin` a pair of whole numbers of copper from 1 to
 * `CURRENCY_MAX` with `min` at most `max`, a chance as an item entry's, and no other field.
 *
 * @param value The entry, a record holding `coin`.
 * @param path Its path, `entries[n]`.
 * @param fail Where a problem goes.
 * @returns The entry, or null.
 */
function coinEntryOf(value: Readonly<Record<string, unknown>>, path: string, fail: (message: string) => void): CoinLootEntry | null {
  let ok = true;
  const coin = value[COIN_FIELD];
  if (!Array.isArray(coin) || coin.length !== 2 || !wholeIn(coin[0], 1, CURRENCY_MAX) || !wholeIn(coin[1], 1, CURRENCY_MAX)) {
    fail(`${path}.coin must be [min, max], whole numbers of copper from 1 to ${CURRENCY_MAX}`);
    ok = false;
  } else if ((coin[0] as number) > (coin[1] as number)) {
    fail(`${path}.coin min ${coin[0] as number} is above max ${coin[1] as number}`);
    ok = false;
  }
  const chance = value["chance"];
  if (!wholeIn(chance, 1, PERCENT)) {
    fail(`${path}.chance must be a whole percent from 1 to ${PERCENT}`);
    ok = false;
  }
  for (const key of Object.keys(value)) {
    if (!(COIN_FIELDS as readonly string[]).includes(key)) {
      fail(`${path}.${key} is not a coin entry field`);
      ok = false;
    }
  }
  return ok ? { coin: [(coin as number[])[0] as number, (coin as number[])[1] as number], chance: chance as number } : null;
}

/**
 * Checks a loot table as its file holds it, each error naming the table and the field: an id equal to the file's name,
 * a name, 1 to 8 entries, each naming an item the rules hold with a quantity from `min` to `max` (1 to 999, `min` at most
 * `max`, `max` at most what an empty bag holds of the item) and a chance in whole percents from 1 to 100, or at most
 * one coin entry, `{ "coin": [min, max], "chance": n }` in copper, and no field a table or an entry does not hold.
 *
 * @param value The parsed file.
 * @param fileId The file's name without `.json`, which the `id` must equal.
 * @param rules The rules whose items the entries name.
 * @returns The table, built from the checked fields, or every error found.
 * @example
 * validateLootTable(JSON.parse(text), "starter-chest", loadRules());
 * // { ok: true, table } or { ok: false, errors: ["starter-chest: entries[0].item names elixir, ..."] }
 */
export function validateLootTable(value: unknown, fileId: string, rules: Rules): LootTableVerdict {
  if (!isPlainRecord(value)) {
    return { ok: false, errors: [`${fileId}: a loot table must be a JSON object`] };
  }
  const errors: string[] = [];
  /**
   * Records one error against the table.
   *
   * @param message What is wrong with which field.
   */
  const fail = (message: string): void => {
    errors.push(`${fileId}: ${message}`);
  };
  const id = value["id"];
  if (typeof id !== "string" || !LOOT_ID.test(id)) {
    fail(`id must be ${LOOT_ID_RULE}`);
  } else if (id !== fileId) {
    fail(`id must be ${fileId}, the file's name, not ${id}`);
  }
  const name = value["name"];
  if (typeof name !== "string" || name.length < 1 || name.length > MAX_LOOT_NAME_CHARS) {
    fail(`name must be text of 1 to ${MAX_LOOT_NAME_CHARS} characters`);
  }
  const list = value["entries"];
  const entries: LootEntry[] = [];
  if (!Array.isArray(list) || list.length < 1 || list.length > MAX_LOOT_ENTRIES) {
    fail(`entries must hold 1 to ${MAX_LOOT_ENTRIES} entries${Array.isArray(list) ? `, not ${list.length}` : ""}`);
  } else {
    let coins = false;
    for (const [index, entry] of list.entries()) {
      if (isPlainRecord(entry) && COIN_FIELD in entry) {
        if (coins) {
          fail(`entries[${index}] is a second coin entry; a table holds at most one`);
          continue;
        }
        coins = true;
      }
      const checked = entryOf(entry, `entries[${index}]`, rules, fail);
      if (checked !== null) {
        entries.push(checked);
      }
    }
  }
  for (const key of Object.keys(value)) {
    if (!(TABLE_FIELDS as readonly string[]).includes(key)) {
      fail(`${key} is not a loot table field`);
    }
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, table: { id: id as string, name: name as string, entries } };
}

/**
 * Rolls a loot table for one character at one chest. Each item entry is rolled on its own from the shared seeded hash
 * over the world, the object, the character and the entry's place: first its chance (an entry of 100 always gives),
 * then a quantity from `min` to `max`. A coin entry gives nothing here, since a chest never gives coins. The same key
 * and table give the same lines on every call and every engine.
 *
 * @param table A table `validateLootTable` admitted.
 * @param key The world, the chest's object id and the character.
 * @returns The lines that give, in entry order, each numbered by its entry's place.
 * @example
 * rollLoot(table, { world: "aethervale", object: "prop-179", character: "0192..." });
 * // [{ line: 0, item: "minor_healing_potion", quantity: 2 }]
 */
export function rollLoot(table: LootTable, key: LootKey): LootLine[] {
  const world = textDigest(key.world, LOOT_SEED);
  const object = textDigest(key.object, LOOT_SEED);
  const character = textDigest(key.character, LOOT_SEED);
  const lines: LootLine[] = [];
  for (const [place, entry] of table.entries.entries()) {
    if (isCoinEntry(entry)) {
      continue;
    }
    const chance = Math.floor(toUnitFloat(hash(LOOT_SEED, world, object, character, place, ROLL_CHANCE)) * PERCENT);
    if (chance >= entry.chance) {
      continue;
    }
    const spread = entry.max - entry.min + 1;
    const quantity = entry.min + Math.floor(toUnitFloat(hash(LOOT_SEED, world, object, character, place, ROLL_QUANTITY)) * spread);
    lines.push({ line: place, item: entry.item, quantity });
  }
  return lines;
}

/**
 * Rolls a creature's loot table for one recipient of one kill (ADR-091's amendment for P2.8-T13, decision 2). Each
 * entry is rolled on its own from the shared seeded hash under `KILL_LOOT_SEED` over the world, the map, the
 * definition, the slot, the tick, the character and the entry's place: first its chance, then a quantity from `min` to
 * `max` for an item or copper from `min` to `max` for coins. The same key and table give the same drop on every call
 * and every engine; the roll is made once, at the fall, and its result travels with the kill's operation.
 *
 * @param table A table `validateLootTable` admitted.
 * @param key The kill and the recipient.
 * @returns The item lines in entry order, each numbered by its entry's place, and the copper, 0 for none.
 * @throws RangeError when the slot or the tick is not a whole number from 0 to 2^32 - 1.
 * @example
 * rollKillLoot(ratDrops, { world: "aethervale", map: "grassland", definition: "spawn-12", slot: 3, tick: 48000, character: "0192..." });
 * // { lines: [], coin: 7 }
 */
export function rollKillLoot(table: LootTable, key: KillLootKey): KillLoot {
  const world = textDigest(key.world, KILL_LOOT_SEED);
  const map = textDigest(key.map, KILL_LOOT_SEED);
  const definition = textDigest(key.definition, KILL_LOOT_SEED);
  const character = textDigest(key.character, KILL_LOOT_SEED);
  const { slot, tick } = key;
  const lines: LootLine[] = [];
  let coin = 0;
  for (const [place, entry] of table.entries.entries()) {
    const chance = Math.floor(toUnitFloat(hash(KILL_LOOT_SEED, world, map, definition, slot, tick, character, place, ROLL_CHANCE)) * PERCENT);
    if (chance >= entry.chance) {
      continue;
    }
    const unit = toUnitFloat(hash(KILL_LOOT_SEED, world, map, definition, slot, tick, character, place, ROLL_QUANTITY));
    if (isCoinEntry(entry)) {
      coin = entry.coin[0] + Math.floor(unit * (entry.coin[1] - entry.coin[0] + 1));
    } else {
      lines.push({ line: place, item: entry.item, quantity: entry.min + Math.floor(unit * (entry.max - entry.min + 1)) });
    }
  }
  return { lines, coin };
}
